import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { generateTimesheetPDF } from './timesheetPDF';
import { useAuth } from './AuthContext';
import './WorkerCalendar.css';

const getBasePath = () => '/databaseOps/';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

const msToTime = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const msToDuration = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (hours === 0) return `${minutes} Min.`;
  if (minutes === 0) return `${hours} Std.`;
  return `${hours} Std. ${minutes} Min.`;
};

interface WorktimeEntry {
  ID: number;
  Datum: number;
  Beginn: number;
  Ende: number;
  Pause: number;
  Bemerkung: string;
  Arbeitszeit: number;
  MitarbeiterID: number;
}

const WorkerCalendar: React.FC = () => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [allEntries, setAllEntries] = useState<WorktimeEntry[]>([]);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [error, setError] = useState('');

  // Load all entries for current worker
  const loadEntries = useCallback(() => {
    fetch(`${getBasePath()}getAllWorktimeOfWorkerID.php`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: new URLSearchParams({}), // worker ID from JWT
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setAllEntries(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Get entries for a specific day (by matching Datum timestamp)
  const getEntriesForDay = (dayMoment: moment.Moment): WorktimeEntry[] => {
    const dayStart = dayMoment.clone().startOf('day').valueOf();
    return allEntries.filter(e => {
      const entryDay = moment(Number(e.Datum)).startOf('day').valueOf();
      return entryDay === dayStart;
    });
  };

  // Build calendar grid
  const buildCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startOfGrid = startOfMonth.clone().startOf('isoWeek'); // Monday
    const endOfGrid = endOfMonth.clone().endOf('isoWeek'); // Sunday

    const days = [];
    const current = startOfGrid.clone();
    while (current.isSameOrBefore(endOfGrid, 'day')) {
      days.push(current.clone());
      current.add(1, 'day');
    }
    return days;
  };

  const calendarDays = buildCalendarDays();

  // Calculate monthly totals
  const monthEntries = allEntries.filter(e => {
    const entryMonth = moment(Number(e.Datum));
    return entryMonth.month() === currentMonth.month() && entryMonth.year() === currentMonth.year();
  });
  const monthTotalMs = monthEntries.reduce((sum, e) => sum + Number(e.Arbeitszeit), 0);

  const handleDelete = async (entryId: number) => {
    try {
      const response = await fetch(`${getBasePath()}deleteWorktime.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id: entryId }),
      });
      const data = await response.json();
      if (data.success) {
        loadEntries();
        setDeleteConfirm(null);
      } else {
        setError(data.error || 'Löschen fehlgeschlagen');
      }
    } catch {
      setError('Netzwerkfehler');
    }
  };

  const today = moment();
  const selectedEntries = selectedDay
    ? getEntriesForDay(moment(selectedDay))
    : [];

  return (
    <div className="worker-calendar">
      <h2>Kalender</h2>

      {error && <div className="calendar-error">{error}</div>}

      <div className="calendar-nav">
        <button onClick={() => setCurrentMonth(m => m.clone().subtract(1, 'month'))}>&lt; Vormonat</button>
        <h3>{currentMonth.format('MMMM YYYY')}</h3>
        <button onClick={() => setCurrentMonth(m => m.clone().add(1, 'month'))}>Nächster &gt;</button>
      </div>

      <div className="calendar-summary">
        <span>Gesamt: <strong>{msToDuration(monthTotalMs)}</strong> ({monthEntries.length} Einträge)</span>
        {monthEntries.length > 0 && (
          <button
            className="btn-print"
            onClick={() => generateTimesheetPDF(
              user?.name || 'Mitarbeiter',
              currentMonth,
              allEntries
            )}
          >
            PDF Stundenzettel
          </button>
        )}
      </div>

      <div className="calendar-grid">
        <div className="calendar-header">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
            <div key={d} className="calendar-header-cell">{d}</div>
          ))}
        </div>
        <div className="calendar-body">
          {calendarDays.map(day => {
            const isCurrentMonth = day.month() === currentMonth.month();
            const isToday = day.isSame(today, 'day');
            const dayEntries = getEntriesForDay(day);
            const hasEntries = dayEntries.length > 0;
            const isSelected = selectedDay && day.isSame(moment(selectedDay), 'day');
            const isWeekend = day.isoWeekday() >= 6;

            let className = 'calendar-day';
            if (!isCurrentMonth) className += ' other-month';
            if (isToday) className += ' today';
            if (hasEntries) className += ' has-entries';
            if (isSelected) className += ' selected';
            if (isWeekend) className += ' weekend';

            return (
              <div
                key={day.format('YYYY-MM-DD')}
                className={className}
                onClick={() => setSelectedDay(day.format('YYYY-MM-DD'))}
              >
                <span className="day-number">{day.date()}</span>
                {hasEntries && (
                  <span className="day-hours">
                    {msToDuration(dayEntries.reduce((s, e) => s + Number(e.Arbeitszeit), 0))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="day-detail">
          <h3>
            {moment(selectedDay).format('dddd, D. MMMM YYYY')}
            {selectedEntries.length === 0 && ' — keine Einträge'}
          </h3>

          {selectedEntries.map(entry => (
            <div key={entry.ID} className="detail-entry">
              <div className="detail-times">
                <span className="detail-range">
                  {msToTime(entry.Beginn)} – {msToTime(entry.Ende)}
                </span>
                {entry.Pause > 0 && (
                  <span className="detail-break">Pause: {msToDuration(entry.Pause)}</span>
                )}
                <span className="detail-net">
                  Netto: {msToDuration(entry.Arbeitszeit)}
                </span>
              </div>
              <div className="detail-remark">{entry.Bemerkung}</div>
              <div className="detail-actions">
                {deleteConfirm === entry.ID ? (
                  <div className="delete-confirm">
                    <span>Wirklich löschen?</span>
                    <button className="btn-confirm-yes" onClick={() => handleDelete(entry.ID)}>Ja</button>
                    <button className="btn-confirm-no" onClick={() => setDeleteConfirm(null)}>Nein</button>
                  </div>
                ) : (
                  <button className="btn-delete" onClick={() => setDeleteConfirm(entry.ID)}>Löschen</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkerCalendar;
